import { Button, FormField, Heading, Stack, TextInput } from 'canoe';
import uniq from 'lodash/uniq';
import React, { useState } from 'react';

import { WebFontResource } from '../types';

const ffRegExp = /font-family:\s*["']*([\w\s-]+)["']*\s*;/g;

/**
 * Fetch CSS file and extract font families
 */
const getFontFamiliesFromCSS = async (url: string) => {
  const res = await fetch(url);
  const data = await res.text();
  const matches = [...data.matchAll(ffRegExp)];
  return uniq(matches.map((m) => m[1]));
};

interface Props {
  onCreate: (name: string, url: string) => Promise<WebFontResource>;
}

export const FontLink = ({ onCreate }: Props) => {
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState('');
  const [error, setError] = useState<null | string>(null);

  const addFont = async () => {
    if (!link) {
      setError('Please provide a valid URL');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const fontFamilies = await getFontFamiliesFromCSS(link);
      if (!fontFamilies.length) {
        setError('Could not find any font families in the supplied CSS');
      } else {
        await Promise.all(fontFamilies.map((name) => onCreate(name, link)));
        setLink('');
      }
    } catch (err) {
      setError(err.response?.data?.message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Stack>
        <Heading size="XXS" element="h2">
          Copy & Paste
        </Heading>

        <FormField
          label="Paste the URL that was provided to you for using your custom fonts online."
          feedback={error ?? ''}
          state={error ? 'danger' : 'none'}
        >
          <TextInput
            placeholder="https://"
            value={link}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLink(e.target.value)}
          />
        </FormField>

        <Button fullWidth variant="secondary" disabled={loading} onClick={addFont}>
          Add
        </Button>
      </Stack>
    </div>
  );
};
